import { parseIsoDateLocal } from "/reports2/report-formatting.js";

/**
 * Report definitions for the reports2 module.
 *
 * Unlike the legacy /reports area (where each report definition comes from the API),
 * the new reports are defined here in the front-end. Definitions are used by both
 * the main landing page (/reports2) and each individual report page.
 *
 * Report IDs:
 *	- Standard reports: RPT-STD-{number}
 *	- Custom reports are phase 2 (spec §11) and have no portal code yet.
 *
 * Availability: every report is gated by user permissions. The permissions API
 * (see reports-service.js getReportPermissionsAsync, GET /Reports2/GetReportPermissions/v1)
 * returns the report IDs available to the current user; each report's own endpoint
 * enforces the same rules and the user's data scope.
 *
 * NOTE: The reports area will be expanded in the future with reports for other
 * modules — add new sections/definitions here following the same pattern.
 */


//---------------------------------------------------------------------------
// Unreleased-report visibility toggle
//---------------------------------------------------------------------------

/**
 * Whether "Phase 2" (section/report `IsPhase2`) and "Next round" (report
 * `IsComingSoon`) content shows on the reports landing page. Off by default so
 * production doesn't expose reports that aren't ready yet — flip to `true`
 * locally when you need to see them in development.
 */
export const showUnreleasedReports = false;



//---------------------------------------------------------------------------
// Sections
//---------------------------------------------------------------------------

/**
 * Sections shown on the reports landing page, in display order.
 */
export const reportSections = [
	{ Code: "TIME", Name: "Time" },
	{ Code: "CLIENT_FACING", Name: "Client-facing" },
	{ Code: "SALES", Name: "Sales & CRM" },
	{ Code: "SPEND", Name: "Spend & budgets", IsPhase2: true },
	{ Code: "WORKFLOW", Name: "Workflow & hygiene" },
	{ Code: "PERSONAL", Name: "Personal" },
	{ Code: "EXISTING", Name: "Existing Reports" },
];



//---------------------------------------------------------------------------
// Date-range presets (spec §7 — standardised picker, default "Last 14 days")
//---------------------------------------------------------------------------

export const dateRangePresets = [
	{ Code: "L7", Name: "Last 7 days" },
	{ Code: "L14", Name: "Last 14 days" },
	{ Code: "TM", Name: "This month" },
	{ Code: "LM", Name: "Last month" },
	{ Code: "TQ", Name: "This quarter" },
	{ Code: "LQ", Name: "Last quarter" },
	{ Code: "TCY", Name: "This calendar year" },
	{ Code: "LCY", Name: "Last calendar year" },
	{ Code: "TFY", Name: "This financial year" },
	{ Code: "LFY", Name: "Last financial year" },
	{ Code: "CUSTOM", Name: "Custom…" },
];


/**
 * The display name for a date-range preset code ("L14" -> "Last 14 days").
 */
export function dateRangePresetName(code)
{
	const preset = dateRangePresets.find((p) => p.Code === code);
	return preset ? preset.Name : code;
}


/**
 * The date range a page's picker model means, as { from: Date, to: Date, isValid }. `model` is the picker's
 * { DateRangePreset, DateFrom, DateTo }. A custom range needs both dates, in order; until it has them `isValid` is false
 * and the range falls back to the default so something sensible is always on screen (the page doesn't run a report on
 * an invalid custom range). Custom dates are bare ISO dates, parsed as LOCAL dates.
 */
export function resolvePageDateRange(model, fyStartMonth = 7)
{
	if (model.DateRangePreset === "CUSTOM")
	{
		const from = parseIsoDateLocal(model.DateFrom);
		const to = parseIsoDateLocal(model.DateTo);

		if (from && to && from <= to) return { from, to, isValid: true };

		const fallback = resolveDateRangePreset("L14", fyStartMonth);
		return { from: fallback.from, to: fallback.to, isValid: false };
	}

	const range = resolveDateRangePreset(model.DateRangePreset || "L14", fyStartMonth);
	return { from: range.from, to: range.to, isValid: true };
}



/**
 * Resolve a date-range preset code to { from: Date, to: Date }.
 * Financial year start is a per-company setting: pass the account's
 * (GetFilterLookups Settings.FinancialYearStartMonth). July is only the fallback.
 */
export function resolveDateRangePreset(code, fyStartMonth = 7)
{
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const D = (y, m, d) => new Date(y, m, d);

	const y = today.getFullYear();
	const m = today.getMonth();
	const q = Math.floor(m / 3);
	const fyStart = m >= (fyStartMonth - 1) ? D(y, fyStartMonth - 1, 1) : D(y - 1, fyStartMonth - 1, 1);

	switch (code)
	{
		case "L7": return { from: D(y, m, today.getDate() - 6), to: today };
		case "TM": return { from: D(y, m, 1), to: today };
		case "LM": return { from: D(y, m - 1, 1), to: D(y, m, 0) };
		case "TQ": return { from: D(y, q * 3, 1), to: today };
		case "LQ": return { from: D(y, (q - 1) * 3, 1), to: D(y, q * 3, 0) };
		case "TCY": return { from: D(y, 0, 1), to: today };
		case "LCY": return { from: D(y - 1, 0, 1), to: D(y - 1, 11, 31) };
		case "TFY": return { from: fyStart, to: today };
		case "LFY": return { from: D(fyStart.getFullYear() - 1, fyStart.getMonth(), 1), to: D(fyStart.getFullYear(), fyStart.getMonth(), 0) };
		case "L14":
		default: return { from: D(y, m, today.getDate() - 13), to: today };
	}
}



//---------------------------------------------------------------------------
// Grouping dimensions for the Time engine (spec §5)
//---------------------------------------------------------------------------

export const timeGroupingDimensions = [
	{ Code: "Team", Name: "Team" },
	{ Code: "Client", Name: "Client" },
	{ Code: "Bucket", Name: "Bucket" },
	{ Code: "Task", Name: "Task" },
	{ Code: "User", Name: "User" },
	{ Code: "Role", Name: "Role" },
	{ Code: "Date", Name: "Date" },
];


/**
 * Grains for the Date dimension (spec §5). The financial year follows the account's own start month.
 */
export const dateGrains = [
	{ Code: "Day", Name: "Day" },
	{ Code: "Week", Name: "Week" },
	{ Code: "Month", Name: "Month" },
	{ Code: "Quarter", Name: "Quarter" },
	{ Code: "Year", Name: "Calendar year" },
	{ Code: "FinancialYear", Name: "Financial year" },
];


/**
 * Billable status filter (spec §5 filters).
 */
export const billingFilterOptions = [
	{ Code: "all", Name: "Billable and non-billable" },
	{ Code: "Billable", Name: "Billable only" },
	{ Code: "NonBillable", Name: "Non-billable only" },
];


export function groupingDimensionName(code)
{
	const dim = timeGroupingDimensions.find((d) => d.Code === code);
	return dim ? dim.Name : code;
}



//---------------------------------------------------------------------------
// Report definitions
//---------------------------------------------------------------------------

/**
 * Shared defaults for the five Time-engine presets (spec §5.1):
 * date range Last 14 days, value basis Rounded, billable split on,
 * approved time only, sorted by the primary dimension, no filters.
 */
const timeEnginePresetDefaults = () => ({
	DateRangePreset: "L14",
	ValueBasis: "Rounded",			// "Rounded" | "Actual"
	BillableSplit: true,
	IncludeUnapproved: false,
	Variant: "explorer",			// "explorer" | "ledger" | "overview" | "cards"
	TertiaryGrouping: "",
	DateGrain: "Day",
	TeamIDs: [],
	ClientIDs: [],
	UserIDs: [],
	RoleIDs: [],
	BucketIDs: [],
	TaskIDs: [],
	Billing: "all",
});


/**
 * All standard report definitions.
 *
 * Properties:
 *	- ID / Name / Description / SectionCode: identity, used on the landing page and report pages.
 *	- RouteTo: where the landing card navigates.
 *	- Engine: "time" for reports that run on the shared Time engine page.
 *	- IsPhase2 / IsComingSoon: display badges.
 *	- ChipInitial / ChipStyle: landing card chip.
 *	- Presets: initial values applied the moment the report opens (one-click reporting —
 *	  a report always loads immediately with these presets). May be a function returning
 *	  calculated values.
 */
export const reportDefinitions = [

	//---- Time (the §5 Time engine, one query surfaced through five presets)
	{
		ID: "RPT-STD-001", Name: "Time by Team", SectionCode: "TIME", Engine: "time",
		Description: "Where a team's time went, and who spent it",
		RouteTo: "/reports2/time/RPT-STD-001",
		ChipInitial: "T",
		Presets: () => ({ ...timeEnginePresetDefaults(), PrimaryGrouping: "Team", SecondaryGrouping: "User", TertiaryGrouping: "Bucket" }),
	},
	{
		ID: "RPT-STD-002", Name: "Time by Client", SectionCode: "TIME", Engine: "time",
		Description: "Who worked on a client, across which buckets, and how much",
		RouteTo: "/reports2/time/RPT-STD-002",
		ChipInitial: "C",
		Presets: () => ({ ...timeEnginePresetDefaults(), PrimaryGrouping: "Client", SecondaryGrouping: "Bucket", TertiaryGrouping: "User" }),
	},
	{
		ID: "RPT-STD-003", Name: "Time by Bucket", SectionCode: "TIME", Engine: "time",
		Description: "How much time a bucket consumed and who spent it",
		RouteTo: "/reports2/time/RPT-STD-003",
		ChipInitial: "B",
		Presets: () => ({ ...timeEnginePresetDefaults(), PrimaryGrouping: "Bucket", SecondaryGrouping: "User", TertiaryGrouping: "Task" }),
	},
	{
		ID: "RPT-STD-004", Name: "Time by User", SectionCode: "TIME", Engine: "time",
		Description: "One person's — or everyone's — time across their work",
		RouteTo: "/reports2/time/RPT-STD-004",
		ChipInitial: "U",
		Presets: () => ({ ...timeEnginePresetDefaults(), PrimaryGrouping: "User", SecondaryGrouping: "Bucket", TertiaryGrouping: "Date", DateGrain: "Day" }),
	},
	{
		ID: "RPT-STD-005", Name: "Time by Role", SectionCode: "TIME", Engine: "time",
		Description: "Time per role tier across the firm — leverage and capacity",
		RouteTo: "/reports2/time/RPT-STD-005",
		ChipInitial: "R",
		Presets: () => ({ ...timeEnginePresetDefaults(), PrimaryGrouping: "Role", SecondaryGrouping: "User", TertiaryGrouping: "Bucket" }),
	},


	//---- Client-facing
	{
		ID: "RPT-STD-006", Name: "Client Time Breakdown", SectionCode: "CLIENT_FACING",
		Description: "A shareable breakdown of the time spent for a client and the commentary logged against it — PDF-first.",
		RouteTo: "/reports2/client-time-breakdown",
		ChipInitial: "CB", ChipStyle: "accent",
		Presets: () => ({ DateRangePreset: "L14", ClientIDs: [], BucketIDs: [], TaskIDs: [] }),
	},
	{
		ID: "RPT-STD-007", Name: "Customer Portal", SectionCode: "CLIENT_FACING", IsPhase2: true,
		Description: "A live, self-serve view for the firm's own clients — their matters, time, and firm-configured integrations behind one login.",
		RouteTo: "/reports2/customer-portal-preview",
		ChipInitial: "P", ChipStyle: "steel",
		Presets: () => ({}),
	},


	//---- Sales & CRM (CRM spec §20.2 and §21). RequiresCrm: shown only to people who can open the CRM reports
	//	   (module.crm/crm-access.js canOpenSalesReports); each report reads only the deals their pipeline visibility allows.
	{
		ID: "RPT-STD-015", Name: "Pipeline by Stage", SectionCode: "SALES", RequiresCrm: true,
		Description: "Open pipeline at each stage — deals, value and weighted value — and how much has gone quiet.",
		RouteTo: "/reports2/sales/pipeline-by-stage",
		ChipInitial: "PS", ChipStyle: "green",
		Presets: () => ({ OwnerUserID: "all", ExpectedCloseCode: "all" }),
	},
	{
		ID: "RPT-STD-016", Name: "Pipeline by Owner", SectionCode: "SALES", RequiresCrm: true,
		Description: "Each owner's open pipeline, weighted value and quiet deals.",
		RouteTo: "/reports2/sales/pipeline-by-owner",
		ChipInitial: "PO", ChipStyle: "green",
		Presets: () => ({ ExpectedCloseCode: "all" }),
	},
	{
		ID: "RPT-STD-017", Name: "Forecast by Quarter", SectionCode: "SALES", RequiresCrm: true,
		Description: "Won plus weighted open pipeline for each quarter of the financial year, against target. Slipped deals shown apart.",
		RouteTo: "/reports2/sales/forecast",
		ChipInitial: "FQ", ChipStyle: "green",
		Presets: () => ({ FinancialYearCode: "", OwnerUserID: "all" }),
	},
	{
		ID: "RPT-STD-018", Name: "Target Attainment", SectionCode: "SALES", RequiresCrm: true,
		Description: "Won value against target for the firm, teams and people you can see, with pipeline coverage of the gap.",
		RouteTo: "/reports2/sales/target-attainment",
		ChipInitial: "TA", ChipStyle: "green",
		Presets: () => ({ PeriodCode: "" }),
	},
	{
		ID: "RPT-STD-019", Name: "Win / Loss", SectionCode: "SALES", RequiresCrm: true,
		Description: "Win rate, why deals were lost and to whom, and conversion by source, product, deal type and stage reached.",
		RouteTo: "/reports2/sales/win-loss",
		ChipInitial: "WL", ChipStyle: "green",
		Presets: () => ({ DateRangePreset: "TFY", OwnerUserID: "all" }),
	},
	{
		ID: "RPT-STD-020", Name: "Aged & Stalled Deals", SectionCode: "SALES", RequiresCrm: true,
		Description: "Open deals by how long since they were touched and how long they've sat in their stage.",
		RouteTo: "/reports2/sales/stalled-deals",
		ChipInitial: "SD", ChipStyle: "green",
		Presets: () => ({ OwnerUserID: "all", View: "quiet" }),
	},
	{
		ID: "RPT-STD-021", Name: "Cost of Acquisition", SectionCode: "SALES", RequiresCrm: true,
		Description: "Pursuit time — and, where you can see cost, its cost at the blended rate — per deal, owner or client.",
		RouteTo: "/reports2/sales/cost-of-acquisition",
		ChipInitial: "CA", ChipStyle: "green",
		Presets: () => ({ DateRangePreset: "TFY", GroupBy: "Deal", Scope: "Closed", OwnerUserID: "all" }),
	},
	{
		ID: "RPT-STD-022", Name: "Activity & Touch Points", SectionCode: "SALES", RequiresCrm: true,
		Description: "Calls, emails, meetings and notes logged on deals, per person or per contact.",
		RouteTo: "/reports2/sales/activity",
		ChipInitial: "AT", ChipStyle: "green",
		Presets: () => ({ DateRangePreset: "LM", GroupBy: "Owner", OwnerUserID: "all" }),
	},


	//---- Spend & budgets (phase 2 — no money in the system yet; built ready)
	{
		ID: "RPT-STD-008", Name: "Bucket Spend", SectionCode: "SPEND", IsPhase2: true,
		Description: "Budget vs consumed effort at sell rates — RAG status, due dates, and overages per bucket.",
		RouteTo: "/reports2/bucket-spend",
		ChipInitial: "$", ChipStyle: "green",
		Presets: () => ({ ClientID: "all", OverBudgetOnly: false }),
	},
	{
		ID: "RPT-STD-009", Name: "Margin by Role", SectionCode: "SPEND", IsPhase2: true,
		Description: "Spend at sell rates vs cost of effort — true margin and leverage per role tier.",
		RouteTo: "/reports2/margin-by-role",
		ChipInitial: "%", ChipStyle: "steel",
		Presets: () => ({ DateRangePreset: "TQ", TeamID: "all", RoleID: "all", ClientID: "all" }),
	},
	{
		ID: "RPT-STD-010", Name: "Rate Cards", SectionCode: "SPEND", IsPhase2: true, IsComingSoon: true,
		Description: "Sell and cost rates per role — the inputs behind spend and margin.",
		RouteTo: null,
		Presets: () => ({}),
	},


	//---- Workflow & hygiene (spec §8)
	{
		ID: "RPT-STD-011", Name: "Unsubmitted Time", SectionCode: "WORKFLOW",
		Description: "Who hasn't submitted yet — outstanding days to chase before close",
		RouteTo: "/reports2/unsubmitted-time",
		ChipInitial: "UT",
		Presets: () => ({ TeamIDs: [], UserIDs: [] }),
	},
	{
		ID: "RPT-STD-012", Name: "Approval Status", SectionCode: "WORKFLOW",
		Description: "Submitted · approved · rejected — where time sits in the approval lifecycle",
		RouteTo: "/reports2/approval-status",
		ChipInitial: "AS",
		Presets: () => ({ DateRangePreset: "L14", TeamIDs: [], UserIDs: [] }),
	},
	{
		ID: "RPT-STD-013", Name: "Needs Allocation", SectionCode: "WORKFLOW",
		Description: "Unallocated entries and capture overlaps to fix before period close",
		RouteTo: "/reports2/needs-allocation",
		ChipInitial: "NA",
		Presets: () => ({ TeamIDs: [], UserIDs: [] }),
	},


	//---- Personal (spec §9 — the user themselves only)
	{
		ID: "RPT-STD-014", Name: "My Time", SectionCode: "PERSONAL",
		Description: "Your own full day — billable, non-billable and personal. Visible to you alone.",
		RouteTo: "/reports2/my-time",
		ChipInitial: "MT",
		Presets: () => ({ DateRangePreset: "L14", ValueBasis: "Rounded" }),
	},

];



//---------------------------------------------------------------------------
// Lookup helpers
//---------------------------------------------------------------------------

/**
 * Find a report definition by ID. Returns null when unknown.
 */
export function getReportDefinitionById(reportId)
{
	return reportDefinitions.find((r) => r.ID === reportId) || null;
}


/**
 * All definitions in a section, in declared order.
 */
export function getReportDefinitionsForSection(sectionCode)
{
	return reportDefinitions.filter((r) => r.SectionCode === sectionCode);
}


/**
 * Resolve a definition's presets (they can be a function returning calculated values).
 */
export function resolveReportPresets(definition)
{
	if (!definition || !definition.Presets) return {};
	return typeof definition.Presets === "function" ? definition.Presets() : { ...definition.Presets };
}
